const DEFAULT_RETRY_LIMIT = 2;
const DEFAULT_RETRY_DELAY = 500; // in milliseconds

// Reusable retry function to handle retries with customizable attempts and delay
/**
 * Runs an async operation and retries it a bounded number of times before giving up. Wraps the attempts in a promise, spacing each retry by the delay and logging the remaining count, then rejects with the final error when the limit is reached.
 *
 * @param fn - Operation retried until it resolves or the limit is reached.
 * @param limit - Maximum number of attempts.
 * @param delay - Milliseconds waited between attempts.
 * @returns A promise resolving to the operation's value once an attempt succeeds.
 */
const retry = <T>(fn: () => Promise<T>, limit = DEFAULT_RETRY_LIMIT, delay = DEFAULT_RETRY_DELAY) =>
  new Promise<T>((resolve, reject) => {
    /**
     * Runs the wrapped operation once and schedules another try after the configured delay when it rejects. Resolves the enclosing promise on success, recurses with one fewer try while tries remain, and rejects with the last error once they run out.
     *
     * @param triesLeft - Number of attempts still allowed, including this one.
     * @returns Nothing.
     */
    const attempt = (triesLeft: number) => {
      fn()
        .then(resolve)
        .catch((error) => {
          if (triesLeft > 1) {
            console.log(`Retrying... Attempts left: ${triesLeft - 1}`);
            setTimeout(() => attempt(triesLeft - 1), delay);
          } else {
            console.error('All retry attempts failed');
            reject(error);
          }
        });
    };
    attempt(limit);
  });

const INDEXEDDB_ERROR_KEY = 'indexedDBError';
const INDEXEDDB_ERROR_DETAIL_KEY = 'indexedDBErrorDetail';

// Latch a session-wide IndexedDB failure plus the real error (name/message) so the report page can
// report why IndexedDB failed instead of a hard-coded string.
const captureIndexedDBError = (error?: unknown) => {
  sessionStorage.setItem(INDEXEDDB_ERROR_KEY, 'true');
  const e = (error ?? {}) as { name?: string; message?: string };
  sessionStorage.setItem(
    INDEXEDDB_ERROR_DETAIL_KEY,
    JSON.stringify({ name: e.name ?? 'Error', message: e.message ?? String(error) }),
  );
};

// Clear the latch once IndexedDB recovers (a successful open or write).
const clearIndexedDBError = () => {
  sessionStorage.removeItem(INDEXEDDB_ERROR_KEY);
  sessionStorage.removeItem(INDEXEDDB_ERROR_DETAIL_KEY);
};

/**
 * Guards that a database handle is open, flagging indexedDBError in sessionStorage and throwing the shared "Database not opened" rejection when it is missing. Called at the top of every helper that needs an open connection; throwing inside a promise executor surfaces as a rejection.
 *
 * @param db - Database handle to check.
 */
const assertDb = (db: IDBDatabase | null) => {
  if (!db) {
    // Flag only; the real open error was already latched by captureIndexedDBError, do not clobber it.
    sessionStorage.setItem(INDEXEDDB_ERROR_KEY, 'true');

    throw new Error('Database not opened');
  }
};

/**
 * Runs one IndexedDB request inside a fresh transaction and resolves it as a promise. Rejects and flags indexedDBError in sessionStorage when the database handle is missing, otherwise opens a transaction on the store, issues the request built by makeRequest, and resolves with mapResult applied to the completed request or rejects with the request's error.
 *
 * @param db - Handle of the already-opened IndexedDB database.
 * @param nameObjectStore - Name of the object store the transaction runs on.
 * @param mode - Transaction mode, readonly or readwrite.
 * @param makeRequest - Builds the IDBRequest to run against the object store.
 * @param mapResult - Maps the completed request to the resolved value; defaults to the request result.
 * @returns A promise resolving to the mapped request result.
 */
const runRequest = <T>(
  db: IDBDatabase,
  nameObjectStore: string,
  mode: IDBTransactionMode,
  makeRequest: (store: IDBObjectStore) => IDBRequest,
  mapResult: (request: IDBRequest) => T = (request) => request.result,
) =>
  new Promise<T>((resolve, reject) => {
    assertDb(db);
    const transaction = db.transaction([nameObjectStore], mode);
    const objectStore = transaction.objectStore(nameObjectStore);
    const request = makeRequest(objectStore);
    request.onsuccess = (event) => resolve(mapResult(event.target as IDBRequest));
    request.onerror = (event) => reject((event.target as IDBRequest).error);
  });

/**
 * Opens an IndexedDB database at version 1 exactly once, creating its object store on first upgrade. Rejects and flags indexedDBError in sessionStorage when the browser exposes no IndexedDB implementation or the open is blocked, and rejects with the request error on failure. This does NOT retry — openDatabase wraps it with a single bounded retry so failures never amplify.
 *
 * @param nameDB - Name of the database to open.
 * @param nameObjectStore - Object store created during the upgrade.
 * @param keyPath - Key path the created object store is indexed by.
 * @returns A promise resolving to the opened database handle.
 */
const openDatabaseOnce = (nameDB: string, nameObjectStore: string, keyPath: string) =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const indexedDB =
      window.indexedDB || window.webkitIndexedDB || window.mozIndexedDB || window.msIndexedDB;
    const request = indexedDB.open(nameDB, 1);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      db.createObjectStore(nameObjectStore, { keyPath });
    };

    request.onsuccess = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      // A successful open means the IndexedDB subsystem recovered; clear the latched error so a
      // transient failure that retried through does not report a false error.
      clearIndexedDBError();
      resolve(db);
    };

    request.onblocked = () => {
      const error = new Error(`Database ${nameDB} open is blocked`);
      captureIndexedDBError(error);
      console.warn(`[${nameDB}] Database open blocked by another connection`);
      reject(error);
    };

    request.onerror = (event) => {
      reject((event.target as IDBOpenDBRequest).error);
    };
  });

/**
 * Opens an IndexedDB database at version 1, retrying a bounded number of times on failure. Rejects immediately (without retrying) and flags indexedDBError in sessionStorage when the browser exposes no IndexedDB implementation; otherwise delegates to openDatabaseOnce and, on the first failure, retries it through the shared retry helper before giving up.
 *
 * @param nameDB - Name of the database to open.
 * @param nameObjectStore - Object store created during the upgrade.
 * @param keyPath - Key path the created object store is indexed by.
 * @returns A promise resolving to the opened database handle.
 */
const openDatabase = (nameDB: string, nameObjectStore: string, keyPath: string) => {
  const indexedDB =
    window.indexedDB || window.webkitIndexedDB || window.mozIndexedDB || window.msIndexedDB;
  if (!indexedDB) {
    captureIndexedDBError(new Error('IndexedDB not supported'));
    return Promise.reject(new Error('IndexedDB not supported'));
  }
  return openDatabaseOnce(nameDB, nameObjectStore, keyPath).catch((error) => {
    console.error(`[${nameDB}] Database error:`, error);
    return retry(() => openDatabaseOnce(nameDB, nameObjectStore, keyPath))
      .then((db) => db)
      .catch((retryError) => {
        // Retries exhausted: latch the real terminal error so the report page can send it up.
        captureIndexedDBError(retryError);
        throw retryError;
      });
  });
};

/**
 * Deletes an entire IndexedDB database by name. Resolves once the delete request succeeds, and rejects with the request error or a still-in-use error when another connection blocks the deletion.
 *
 * @param nameDB - Name of the database to delete.
 * @returns A promise resolving once the database has been deleted.
 */
const deleteDatabase = (nameDB: string) =>
  new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(nameDB);
    request.onsuccess = () => {
      resolve();
    };
    request.onerror = (event) => {
      console.error(
        `Failed to delete database ${nameDB}`,
        (event.target as IDBOpenDBRequest).error,
      );
      reject((event.target as IDBOpenDBRequest).error);
    };

    request.onblocked = () => {
      console.log(`Database ${nameDB} is still being used`);
      reject(new Error(`Database ${nameDB} is still being used`));
    };
  });

/**
 * Inserts a key and value pair into an object store as a new record. Rejects and flags indexedDBError in sessionStorage when the database handle is missing, otherwise runs an add request in a readwrite transaction and resolves with its result.
 *
 * @param db - Handle of the already-opened IndexedDB database.
 * @param nameObjectStore - Name of the object store to operate on.
 * @param key - Key the record is stored under.
 * @param value - Payload wrapped alongside the key in the stored record.
 * @returns A promise resolving to the key produced by the add request.
 */
const addData = (db: IDBDatabase, nameObjectStore: string, key: IDBValidKey, value: any) =>
  runRequest(db, nameObjectStore, 'readwrite', (store) => store.add({ key, value }));

/**
 * Puts every record of an array into one object store within a single readwrite transaction. Rejects and flags indexedDBError in sessionStorage when the database handle is missing, and resolves once the transaction completes with the array of keys produced by the puts.
 *
 * @param db - Handle of the already-opened IndexedDB database.
 * @param nameObjectStore - Name of the object store to operate on.
 * @param dataArray - Records put into the store.
 * @returns A promise resolving to the array of keys produced by the put requests once the transaction commits.
 */
const addMultipleData = (db: IDBDatabase, nameObjectStore: string, dataArray: any[]) =>
  new Promise((resolve, reject) => {
    assertDb(db);
    const transaction = db.transaction([nameObjectStore], 'readwrite');
    const objectStore = transaction.objectStore(nameObjectStore);
    const results: any[] = [];
    dataArray.forEach((data) => {
      const request = objectStore.put(data);
      request.onsuccess = (event) => results.push((event.target as IDBRequest).result);
    });
    transaction.oncomplete = () => resolve(results);
    transaction.onerror = (event) => reject((event.target as IDBTransaction).error);
  });

/**
 * Reads one record from an object store by key. Rejects and flags indexedDBError in sessionStorage when the handle is missing, otherwise runs a get request in a readonly transaction.
 *
 * @param db - Handle of the already-opened IndexedDB database.
 * @param nameObjectStore - Name of the object store to operate on.
 * @param key - Key of the record to read.
 * @returns A promise resolving to the stored record for that key.
 */
const getData = <T = any>(db: IDBDatabase, nameObjectStore: string, key: IDBValidKey) =>
  runRequest<T>(db, nameObjectStore, 'readonly', (store) => store.get(key));

/**
 * Reads every record from one object store. Rejects and flags indexedDBError in sessionStorage when the handle is missing, otherwise runs a getAll request in a readonly transaction.
 *
 * @param db - Handle of the already-opened IndexedDB database.
 * @param nameObjectStore - Name of the object store to operate on.
 * @returns A promise resolving to the array of all records in the store.
 */
const getAllData = <T = any>(db: IDBDatabase, nameObjectStore: string) =>
  runRequest<T[]>(db, nameObjectStore, 'readonly', (store) => store.getAll());

/**
 * Reads only the primary keys of one object store, without loading the records. Rejects and flags indexedDBError in sessionStorage when the handle is missing, otherwise runs a getAllKeys request in a readonly transaction — cheap because record payloads are never materialised.
 *
 * @param db - Handle of the already-opened IndexedDB database.
 * @param nameObjectStore - Name of the object store to operate on.
 * @returns A promise resolving to the array of stored keys.
 */
const getAllKeys = <T extends IDBValidKey = IDBValidKey>(
  db: IDBDatabase,
  nameObjectStore: string,
) => runRequest<T[]>(db, nameObjectStore, 'readonly', (store) => store.getAllKeys());

/**
 * Overwrites the record stored under a key in one object store. Rejects and flags indexedDBError in sessionStorage when the handle is missing, otherwise runs a put request in a readwrite transaction.
 *
 * @param db - Handle of the already-opened IndexedDB database.
 * @param nameObjectStore - Name of the object store to operate on.
 * @param key - Key of the record to overwrite.
 * @param value - Payload wrapped alongside the key in the stored record.
 * @returns A promise resolving to the key produced by the put request.
 */
const updateData = (db: IDBDatabase, nameObjectStore: string, key: IDBValidKey, value: any) =>
  runRequest(db, nameObjectStore, 'readwrite', (store) => store.put({ key, value }));

/**
 * Deletes one record from an object store by key. Rejects and flags indexedDBError in sessionStorage when the handle is missing, otherwise runs a delete request in a readwrite transaction and resolves with the key.
 *
 * @param db - Handle of the already-opened IndexedDB database.
 * @param nameObjectStore - Name of the object store to operate on.
 * @param key - Key of the record to delete.
 * @returns A promise resolving to the deleted key.
 */
const deleteData = (db: IDBDatabase, nameObjectStore: string, key: IDBValidKey) =>
  runRequest(
    db,
    nameObjectStore,
    'readwrite',
    (store) => store.delete(key),
    () => key,
  );

/**
 * Writes a key and value pair into an object store, adding it when no record exists yet. Rejects and flags indexedDBError in sessionStorage when the handle is missing, otherwise reads the key first and issues either a put or an add, resolving with null and warning when the value cannot be structured-cloned.
 *
 * @param db - Handle of the already-opened IndexedDB database.
 * @param nameObjectStore - Name of the object store to operate on.
 * @param key - Key to write under.
 * @param value - Payload wrapped alongside the key in the stored record.
 * @returns A promise resolving to the key produced by the put or add, or null when the value could not be cloned.
 */
const upsertData = (db: IDBDatabase, nameObjectStore: string, key: IDBValidKey, value: any) =>
  new Promise((resolve, reject) => {
    assertDb(db);
    const transaction = db.transaction([nameObjectStore], 'readwrite');
    const objectStore = transaction.objectStore(nameObjectStore);
    const request = objectStore.get(key);

    request.onsuccess = function () {
      try {
        const writeRequest = request.result
          ? objectStore.put({ key, value })
          : objectStore.add({ key, value });
        writeRequest.onsuccess = function (event) {
          resolve((event.target as IDBRequest).result);
        };
        writeRequest.onerror = function (event) {
          reject((event.target as IDBRequest).error);
        };
      } catch (error) {
        console.warn('[IDB upsert DataCloneError] key =', key, error);
        resolve(null);
      }
    };

    request.onerror = function (event) {
      reject((event.target as IDBRequest).error);
    };
  });

/**
 * Reads a record and writes a transformed value back within a single readwrite transaction, so the read and write cannot interleave with another writer. Rejects and flags indexedDBError in sessionStorage when the handle is missing; runs transform against the current stored record (or undefined when absent) and skips the write, resolving null, when transform returns undefined; otherwise puts the transformed value and resolves with the put key.
 *
 * @param db - Handle of the already-opened IndexedDB database.
 * @param nameObjectStore - Name of the object store to operate on.
 * @param key - Key of the record to read and overwrite.
 * @param transform - Maps the current record to the new value, or returns undefined to skip the write.
 * @returns A promise resolving to the key produced by the put, or null when the write was skipped.
 */
const readModifyWriteData = <T = any>(
  db: IDBDatabase,
  nameObjectStore: string,
  key: IDBValidKey,
  transform: (current: T | undefined) => any,
) =>
  new Promise((resolve, reject) => {
    assertDb(db);
    const transaction = db.transaction([nameObjectStore], 'readwrite');
    const objectStore = transaction.objectStore(nameObjectStore);
    const getRequest = objectStore.get(key);

    getRequest.onsuccess = (event) => {
      try {
        const nextValue = transform((event.target as IDBRequest).result as T | undefined);
        if (nextValue === undefined) {
          resolve(null);
          return;
        }
        const writeRequest = objectStore.put({ key, value: nextValue });
        writeRequest.onsuccess = (event) => resolve((event.target as IDBRequest).result);
        writeRequest.onerror = (event) => reject((event.target as IDBRequest).error);
      } catch (error) {
        reject(error);
      }
    };

    getRequest.onerror = (event) => reject((event.target as IDBRequest).error);
  });

/**
 * Removes every record from one object store. Rejects and flags indexedDBError in sessionStorage when the handle is missing, resolves with a notice when the store does not exist, rejects when the connection is already closing, and otherwise runs a clear request in a readwrite transaction.
 *
 * @param db - Handle of the already-opened IndexedDB database.
 * @param nameObjectStore - Name of the object store to operate on.
 * @returns A promise resolving to the clear request result, or to a message saying the store does not exist.
 */
const clearObjectStore = (db: IDBDatabase, nameObjectStore: string) => {
  if (db && !db.objectStoreNames.contains(nameObjectStore)) {
    return Promise.resolve(`Object store ${nameObjectStore} does not exist`);
  }
  // closePending is not part of the standard IDBDatabase type
  if (db && (db as any).closePending) {
    return Promise.reject(new Error('Database connection is closing'));
  }
  return runRequest(db, nameObjectStore, 'readwrite', (store) => store.clear());
};

/**
 * Deletes every record whose numeric key sits after a given index, trimming the undo history tail. Rejects and flags indexedDBError in sessionStorage when the database handle is missing, otherwise walks a key cursor from index plus one upward deleting each primary key until the cursor is exhausted.
 *
 * @param db - Handle of the already-opened IndexedDB database.
 * @param objectStoreName - Name of the object store to operate on.
 * @param index - Last index kept; everything above it is deleted.
 * @returns A promise resolving once all records past the index have been deleted.
 */
const clearDataAfterIndex = (db: IDBDatabase, objectStoreName: string, index: number) =>
  new Promise<void>((resolve, reject) => {
    assertDb(db);
    const transaction = db.transaction([objectStoreName], 'readwrite');
    const store = transaction.objectStore(objectStoreName);
    const keyRange = IDBKeyRange.lowerBound(index + 1, false);
    const request = store.openKeyCursor(keyRange);

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursor | null>).result;
      if (cursor) {
        store.delete(cursor.primaryKey);
        cursor.continue();
      } else {
        resolve();
      }
    };

    request.onerror = (event) => {
      reject((event.target as IDBRequest).error);
    };
  });

/**
 * Checks whether any undo history entry at or before an index was recorded under a given action name. Rejects and flags indexedDBError in sessionStorage when the handle is missing, otherwise walks a readonly cursor bounded at the index and resolves true on the first matching actionName or false once the cursor is exhausted.
 *
 * @param db - Handle of the already-opened IndexedDB database.
 * @param nameObjectStore - Name of the object store to operate on.
 * @param actionName - History action name being looked for.
 * @param index - Upper bound of the cursor range, inclusive.
 * @returns A promise resolving to true when an entry with that action name exists at or before the index.
 */
const keyExistsBeforeIndex = (
  db: IDBDatabase,
  nameObjectStore: string,
  actionName: string,
  index: number,
) =>
  new Promise((resolve, reject) => {
    assertDb(db);

    const transaction = db.transaction([nameObjectStore], 'readonly');
    const store = transaction.objectStore(nameObjectStore);

    const keyRange = IDBKeyRange.upperBound(index, false);
    const request = store.openCursor(keyRange);

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue | null>).result;
      if (cursor) {
        if ((cursor.value as any).value.actionName === actionName) {
          resolve(true);
        } else {
          cursor.continue();
        }
      } else {
        resolve(false);
      }
    };

    request.onerror = (event) => {
      reject((event.target as IDBRequest).error);
    };
  });

/**
 * Counts the records held in one object store. Rejects and flags indexedDBError in sessionStorage when the handle is missing, otherwise runs a count request in a readonly transaction.
 *
 * @param db - Handle of the already-opened IndexedDB database.
 * @param nameObjectStore - Name of the object store to operate on.
 * @returns A promise resolving to the number of records in the store.
 */
const countData = (db: IDBDatabase, nameObjectStore: string) =>
  runRequest<number>(db, nameObjectStore, 'readonly', (store) => store.count());

// Reopening the connection and retrying only helps for a stale/closed handle or a
// transient transaction fault. These names describe the value itself, so a fresh
// connection would fail identically — retrying them is pointless (Quota is handled
// separately by beat-store eviction).
const NON_RETRYABLE_WRITE_ERRORS = new Set([
  'QuotaExceededError',
  'DataCloneError',
  'ConstraintError',
]);

/**
 * Decides whether a failed write is worth reopening the connection and retrying once. Returns false for errors that a fresh connection cannot fix (quota, non-cloneable value, key constraint) and true for everything else, so callers only retry transient or stale-handle faults.
 *
 * @param error - The rejection reason from a write operation.
 * @returns True when the write should be retried after reopening the database.
 */
const isRetryableWriteError = (error: unknown) =>
  !NON_RETRYABLE_WRITE_ERRORS.has((error as { name?: string })?.name ?? '');

export {
  openDatabase,
  captureIndexedDBError,
  clearIndexedDBError,
  isRetryableWriteError,
  deleteDatabase,
  addData,
  addMultipleData,
  getData,
  getAllData,
  getAllKeys,
  updateData,
  deleteData,
  upsertData,
  readModifyWriteData,
  clearObjectStore,
  clearDataAfterIndex,
  keyExistsBeforeIndex,
  countData,
};
